"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { Button } from "@/components/ui/button";
import {
  Download,
  Settings,
  RotateCcw,
  Sparkles,
  MoreHorizontal,
  CalendarDays,
  CalendarRange,
  Cloud,
  Calendar,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useSubscription } from "@/components/SubscriptionContext";
import { useAuth } from "@clerk/nextjs";
import { type Event } from "@/lib/types";
import { AI_ENABLED } from "@/lib/ai-config";

// Dynamically import dialog components for code splitting
const AuthModal = dynamic(
  () => import("@/components/AuthModal").then((m) => m.AuthModal),
  { ssr: false },
);
const CloudSaveDialog = dynamic(
  () => import("@/components/CloudSaveDialog").then((m) => m.CloudSaveDialog),
  { ssr: false },
);
const CalendarSyncDialog = dynamic(
  () =>
    import("@/components/CalendarSyncDialog").then((m) => m.CalendarSyncDialog),
  { ssr: false },
);
import { useSettings } from "@/components/SettingsContext";
import { AIQuickActions } from "@/components/templates/AIQuickActions";
import { hasDataToResetFromStorage } from "@/components/ResetButton";

const FeatureComingSoonModal = dynamic(
  () =>
    import("@/components/FeatureComingSoonModal").then(
      (m) => m.FeatureComingSoonModal,
    ),
  { ssr: false },
);
const SettingsDialog = dynamic(
  () => import("@/components/SettingsDialog").then((m) => m.SettingsDialog),
  { ssr: false },
);
const AIAutofillDialog = dynamic(
  () => import("@/components/AIAutofillDialog").then((m) => m.AIAutofillDialog),
  { ssr: false },
);

import { useTranslations } from "next-intl";

interface MobileToolbarProps {
  onReset: () => void;
  viewMode: "day" | "week";
  onViewModeChange: (mode: "day" | "week") => void;
  weekStart: Date;
  weekStartsOnSunday: boolean;
  onExport: () => void;
  onLoadSchedule?: (
    events: Event[],
    settings: Record<string, unknown> | null,
  ) => void;
  showSettingsOpen?: boolean;
  onSettingsOpenChange?: (open: boolean) => void;
  showAIAutofillOpen?: boolean;
  onAIAutofillOpenChange?: (open: boolean) => void;
  onAddEvents?: (events: Omit<Event, "id">[]) => void;
}

export function MobileToolbar({
  onReset,
  viewMode,
  onViewModeChange,
  weekStart,
  weekStartsOnSunday,
  onExport,
  onLoadSchedule,
  showSettingsOpen,
  onSettingsOpenChange,
  showAIAutofillOpen,
  onAIAutofillOpenChange,
  onAddEvents,
}: MobileToolbarProps) {
  const t = useTranslations("Common");
  const { userId } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);

  // Sync external state if provided
  const effectiveShowSettings =
    showSettingsOpen !== undefined ? showSettingsOpen : showSettingsDialog;
  const setEffectiveSettingsOpen = (open: boolean) => {
    if (onSettingsOpenChange) {
      onSettingsOpenChange(open);
    } else {
      setShowSettingsDialog(open);
    }
  };
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [showCloudSaveDialog, setShowCloudSaveDialog] = useState(false);
  const [showCalendarSyncDialog, setShowCalendarSyncDialog] = useState(false);
  const [comingSoonFeature] = useState("");
  const [comingSoonDescription] = useState<React.ReactNode>(null);
  const [showAIAutofillDialog, setShowAIAutofillDialog] = useState(false);

  // Sync external AI Autofill state if provided
  const effectiveShowAIAutofill =
    showAIAutofillOpen !== undefined
      ? showAIAutofillOpen
      : showAIAutofillDialog;
  const setEffectiveAIAutofillOpen = (open: boolean) => {
    if (onAIAutofillOpenChange) {
      onAIAutofillOpenChange(open);
    } else {
      setShowAIAutofillDialog(open);
    }
  };

  const { isLoading } = useSubscription();
  const { settings } = useSettings();

  const handleExportClick = () => {
    setShowMoreSheet(false);
    onExport();
  };

  const handleAIAutofillClick = () => {
    if (isLoading) return;
    setShowMoreSheet(false);

    // Requirement: Unauthenticated users see login prompt
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    // Authenticated users (Free or Pro) open the dialog
    setEffectiveAIAutofillOpen(true);
  };

  const handleResetClick = () => {
    if (!hasDataToResetFromStorage(settings)) {
      return;
    }

    setShowMoreSheet(false);
    setShowResetDialog(true);
  };

  const handleSettingsClick = () => {
    setShowMoreSheet(false);
    setEffectiveSettingsOpen(true);
  };

  const handleCloudSaveClick = () => {
    if (isLoading) return;

    // Requirement: Unauthenticated users see login prompt
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    // Authenticated users (Free or Pro) open the cloud save dialog
    // The dialog itself will handle tiered storage limits
    setShowCloudSaveDialog(true);
  };

  const handleCalendarSyncClick = () => {
    setShowMoreSheet(false);

    // Requirement: Unauthenticated users see login prompt
    if (!userId) {
      setShowAuthModal(true);
      return;
    }

    // All users (Free and Pro) can use Calendar Sync
    setShowCalendarSyncDialog(true);
  };

  return (
    <>
      {/* Floating Action Buttons - Stacked on the right */}
      <div className="fixed bottom-[calc(5rem_+_env(safe-area-inset-bottom,0px))] right-4 z-50 flex flex-col gap-3 items-center md:hidden pointer-events-none">
        {/* AI Quick Actions - Only for AI Schedule Builder Template */}
        {AI_ENABLED &&
          settings.activeTemplateSlug === "ai-schedule-builder" && (
            <div className="pointer-events-auto">
              <AIQuickActions
                variant="compact"
                onRegenerateSchedule={() => setEffectiveAIAutofillOpen(true)}
                onClearSchedule={onReset}
              />
            </div>
          )}

        {/* AI Autofill FAB - always visible */}
        {AI_ENABLED && (
          <button
            onClick={handleAIAutofillClick}
            aria-label={t("sidebar.aiAutofill")}
            title={t("sidebar.aiAutofill")}
            className="pointer-events-auto size-14 rounded-full shadow-lg bg-gradient-to-r from-violet-500 to-blue-500 hover:from-violet-600 hover:to-blue-600 text-white flex items-center justify-center shimmer hover:glow-primary transition-shadow duration-300"
          >
            <Sparkles className="size-6" />
          </button>
        )}
      </div>

      {/* Fixed Bottom Toolbar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 px-4 py-2 md:hidden safe-area-pb">
        <div className="flex items-center justify-around">
          {/* Reset Button (4) */}
          <button
            onClick={handleResetClick}
            className="flex flex-col items-center gap-0.5 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <RotateCcw className="size-5 text-gray-600 dark:text-gray-400" />
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {t("sidebar.reset")}
            </span>
          </button>

          {/* Export Button (3) */}
          <button
            onClick={handleExportClick}
            className="flex flex-col items-center gap-0.5 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Download className="size-5 text-gray-600 dark:text-gray-400" />
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {t("export")}
            </span>
          </button>

          {/* Day/Week Toggle Button (1) */}
          <button
            onClick={() =>
              onViewModeChange(viewMode === "day" ? "week" : "day")
            }
            className="flex flex-col items-center gap-0.5 p-2 w-16 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            {viewMode === "day" ? (
              <CalendarDays className="size-5 text-blue-600 dark:text-blue-400" />
            ) : (
              <CalendarRange className="size-5 text-blue-600 dark:text-blue-400" />
            )}
            <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
              {viewMode === "day" ? t("viewMode.day") : t("viewMode.week")}{" "}
              <span className="text-gray-400 dark:text-gray-500">↔</span>
            </span>
          </button>

          {/* Cloud Save Button (2) */}
          <button
            onClick={handleCloudSaveClick}
            className="flex flex-col items-center gap-0.5 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <Cloud className="size-5 text-gray-600 dark:text-gray-400" />
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {t("cloud")}
            </span>
          </button>

          {/* More Menu (5) */}
          <Sheet open={showMoreSheet} onOpenChange={setShowMoreSheet}>
            <SheetTrigger asChild>
              <button className="flex flex-col items-center gap-0.5 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
                <MoreHorizontal className="size-5 text-gray-600 dark:text-gray-400" />
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {t("more")}
                </span>
              </button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="h-auto rounded-t-2xl dark:bg-gray-900"
            >
              <SheetHeader className="pb-4">
                <SheetTitle>{t("moreOptions")}</SheetTitle>
                <SheetDescription className="sr-only">
                  {t("moreOptionsDescription")}
                </SheetDescription>
              </SheetHeader>
              <div className="grid grid-cols-3 gap-4 pb-6">
                {/* Sync Button (4) */}
                <button
                  onClick={handleCalendarSyncClick}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <Calendar className="size-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {t("sync")}
                  </span>
                </button>

                {/* AI Fill Button (2) */}
                {AI_ENABLED && (
                  <button
                    onClick={handleAIAutofillClick}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-violet-500 to-blue-500 flex items-center justify-center">
                      <Sparkles className="size-5 text-white" />
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      {t("aiFill")}
                    </span>
                  </button>
                )}

                {/* Settings Button (1) */}
                <button
                  onClick={handleSettingsClick}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                    <Settings className="size-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    {t("sidebar.settings")}
                  </span>
                </button>

              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Reset Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("reset.title")}</DialogTitle>
            <DialogDescription>{t("reset.description")}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              {t("reset.cancel")}
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => {
                onReset();
                setShowResetDialog(false);
              }}
            >
              {t("reset.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <SettingsDialog
        open={effectiveShowSettings}
        onOpenChange={setEffectiveSettingsOpen}
      />

      {/* Feature Coming Soon Modal */}
      <FeatureComingSoonModal
        open={showComingSoonModal}
        onOpenChange={setShowComingSoonModal}
        featureName={comingSoonFeature}
        description={comingSoonDescription}
      />

      {/* Cloud Save Dialog */}
      <CloudSaveDialog
        open={showCloudSaveDialog}
        onOpenChange={setShowCloudSaveDialog}
        onLoadSchedule={(events, settings) => {
          if (onLoadSchedule) {
            onLoadSchedule(events, settings);
          }
        }}
      />

      {/* Calendar Sync Dialog */}
      <CalendarSyncDialog
        open={showCalendarSyncDialog}
        onOpenChange={setShowCalendarSyncDialog}
        weekStart={weekStart}
        weekStartsOnSunday={weekStartsOnSunday}
      />

      {/* AI Autofill Dialog */}
      {AI_ENABLED && (
        <AIAutofillDialog
          open={effectiveShowAIAutofill}
          onOpenChange={setEffectiveAIAutofillOpen}
          onAddEvents={(events) => {
            if (onAddEvents) {
              onAddEvents(events);
            }
          }}
          weekStart={weekStart}
          weekStartsOnSunday={weekStartsOnSunday}
          onReset={onReset}
        />
      )}

      {/* Auth Modal */}
      <AuthModal
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
        defaultMode="sign-up"
      />
    </>
  );
}
